import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { siteInfo, siteNav } from '@/mocks/site';

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goAnchor = (hash: string) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 140);
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-background-50 border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-water-flash-line text-background-50 text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-wide">
              {siteInfo.name}
            </span>
            <span className="hidden sm:block text-[11px] text-foreground-500 mt-1 tracking-wider">
              {siteInfo.slogan}
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center" aria-label="主导航">
          {siteNav.map((item) =>
            item.to.startsWith('/#') ? (
              <button
                key={item.label}
                type="button"
                onClick={() => goAnchor(item.to.replace('/', ''))}
                className="px-4 py-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {item.label}
              </button>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className={`px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === item.to
                    ? 'text-primary-600 font-medium'
                    : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                {item.label}
              </Link>
            ),
          )}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/about"
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-send-plane-line"></i>
            我要投稿
          </Link>
          <button
            type="button"
            aria-label={open ? '关闭菜单' : '打开菜单'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
          >
            <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          <nav className="flex flex-col" aria-label="移动端导航">
            {siteNav.map((item) =>
              item.to.startsWith('/#') ? (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => goAnchor(item.to.replace('/', ''))}
                  className="text-left py-3 px-2 text-base text-foreground-800 border-b border-background-100 cursor-pointer"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className="py-3 px-2 text-base text-foreground-800 border-b border-background-100 cursor-pointer"
                >
                  {item.label}
                </Link>
              ),
            )}
            <Link
              to="/about"
              className="mt-3 mb-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-line"></i>
              我要投稿
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}